import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportsArtifact } from '@versesverse/schema/artifacts';
import type { PanelReport } from './ReportsList';

/*
 * OPENING A FIGURE'S HADITH, ON DEMAND.
 *
 * The bundle carries only how MANY reports name each figure. The reports themselves are Arabic in
 * bulk, so they arrive when a panel is opened and never before — the same bargain the verse ranges
 * strike, for the same reason.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * AND IN PAGES SINCE 2026-08-20, WHICH IS A LARGER CHANGE THAN IT LOOKS.
 *
 * A file per figure was right at 12,370 reports and broke at 151,363: **`abu-hurayra.json` reached
 * 19.2 MB**, ʿĀʾisha 16.7, Ibn ʿAbbās 15.2 — after eight books from one supplier added 49,759
 * reports in a day. Opening one figure downloaded nineteen megabytes before a word was drawn.
 *
 * So a figure's file is cut into pages and this fetches the first, then more as the reader asks.
 * `ReportsList` already grows twenty at a time on scroll; **the file behind it was the only part
 * that did not**, and `loadMore` is what closes that gap.
 *
 * A FAILED FETCH IS NOT AN EMPTY RESULT. `status` distinguishes them, because "this figure has no
 * hadith" and "we could not reach them" are different facts and the panel must not render the
 * second as the first — the count in the bundle already told the reader there were some.
 *
 * A FAILED SECOND PAGE IS NOT A FAILED PANEL EITHER. Reports already shown stay shown; only
 * `loading` goes false and `hasMore` stays true, so the reader can scroll again and retry. Losing a
 * page a reader is looking at because the next one timed out would be the worse of the two.
 */
export interface ReportSource {
  reports(node: string, page?: number): Promise<ReportsArtifact>;
}

export interface ReportsState {
  status: 'idle' | 'loading' | 'ready' | 'failed';
  reports: PanelReport[];
  /**
   * How many of the figure's reports they only PASSED ON, over the whole list — not over the pages
   * that happen to have loaded, which is the distinction the panel got wrong. See
   * `schema/artifacts.ts`; absent on a figure who passed none on, and on a file built before it.
   */
  narrated?: number;
  /** Whether another page exists. False on a single-page figure, which is most of them. */
  hasMore: boolean;
  /** Fetch the next page. A no-op while one is in flight, or when there is nothing left. */
  loadMore: () => void;
}

const NOOP = (): void => { /* no page to ask for */ };

export function useNodeReports(
  nodeId: string | null,
  /** From the bundle. Absent or zero means there is nothing to fetch. */
  count: number | undefined,
  source: ReportSource,
): ReportsState {
  const [state, setState] = useState<{
    status: ReportsState['status']; reports: PanelReport[]; page: number; pages: number; narrated?: number;
  }>({ status: 'idle', reports: [], page: -1, pages: 1 });

  /*
   * The figure a response belongs to, and whether a fetch is already out.
   *
   * A panel can close, or the reader can travel to another figure, before a fetch lands. Applying
   * a stale response would show one figure's hadith under another's name — so every response
   * checks the id it was asked for, rather than a boolean that only knows "something changed".
   */
  const wanted = useRef<string | null>(null);
  const inFlight = useRef(false);

  const fetchPage = useCallback((node: string, page: number) => {
    if (inFlight.current) return;
    inFlight.current = true;
    source.reports(node, page)
      .then(a => {
        if (wanted.current !== node) return;
        setState(s => ({
          status: 'ready',
          /* Appended, not replaced: page 0 is a fresh list and later pages extend it. */
          reports: page === 0 ? a.reports : [...s.reports, ...a.reports],
          page,
          pages: a.pages ?? 1,
          /* Counted over the whole list by the build — see `schema/artifacts.ts`. Written on every
             page, so it is known from the first fetch; kept if a later page omits it. */
          narrated: a.narrated ?? s.narrated,
        }));
      })
      .catch(() => {
        if (wanted.current !== node) return;
        /* A first page that fails is a failed panel; a later one leaves what is already shown. */
        setState(s => (page === 0 ? { ...s, status: 'failed', reports: [] } : { ...s, status: 'ready' }));
      })
      .finally(() => { inFlight.current = false; });
  }, [source]);

  useEffect(() => {
    wanted.current = nodeId;
    /*
     * THE IN-FLIGHT GUARD IS CLEARED WHEN THE FIGURE CHANGES, AND FORGETTING THAT HUNG THE PANEL.
     *
     * `inFlight` exists to stop a second page being asked for twice while the first is out. Left
     * set across a change of figure it also blocked the NEW figure's page 0 — so a reader who
     * travelled while a fetch was in the air met a panel stuck on "loading" for ever. The existing
     * test for a late response caught it, which is what that test is for.
     *
     * The abandoned request is not cancelled and does not need to be: its response is discarded by
     * the `wanted` check, which is the same guard that stops one figure's hadith appearing under
     * another's name.
     */
    inFlight.current = false;
    if (!nodeId || !count) { setState({ status: 'idle', reports: [], page: -1, pages: 1 }); return; }
    setState({ status: 'loading', reports: [], page: -1, pages: 1 });
    fetchPage(nodeId, 0);
    return () => { wanted.current = null; };
  }, [nodeId, count, fetchPage]);

  const hasMore = state.page >= 0 && state.page + 1 < state.pages;
  const loadMore = useCallback(() => {
    if (!nodeId || !hasMore) return;
    fetchPage(nodeId, state.page + 1);
  }, [nodeId, hasMore, state.page, fetchPage]);

  return { status: state.status, reports: state.reports, narrated: state.narrated, hasMore, loadMore: hasMore ? loadMore : NOOP };
}
